<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $t('views.aftersales_sales_report.title') }}
      </h1>
      <div class="ml-auto">
        <v-btn
          outlined
          large
          @click="toggleFilter"
        >
          <v-icon left>
            mdi-filter-variant
          </v-icon>
          {{ $t('common.filter') }}
        </v-btn>
      </div>
    </div>

    <sales-report-filter
      :is-shown="isFilterShown"
      :filter="filter"
      @applyFilter="applyFilter"
    />

    <main class="basket-page__main">
      <v-data-table
        fixed-header
        :headers="headers"
        :items="items"
        item-key="orderPartId"
        :group-by="filter.group"
        :options.sync="options"
        :server-items-length="total"
        hide-default-footer
        :loading="loading"
        :loading-text="$t('common.loading_data')"
      >
        <template
          v-slot:group.header="{
            group, headers: groupHeaders, toggle, isOpen, items: groupItems, groupBy }"
        >
          <td :colspan="groupHeaders.length - 3">
            <v-btn
              :ref="group"
              small
              icon
              :data-open="isOpen"
              @click="toggle"
            >
              <v-icon v-if="isOpen">
                mdi-chevron-up
              </v-icon>
              <v-icon v-else>
                mdi-chevron-down
              </v-icon>
            </v-btn>
            {{ groupByHeader(groupBy, group) }}
          </td>
          <td>
            <span>{{ sumSubTotal(groupItems) }}</span>
          </td>
          <td>
            <span>{{ groupItems[0].currency }}</span>
          </td>
          <td><span>&nbsp;</span></td>
        </template>
        <template v-slot:item.number="{ item }">
          <router-link :to="`/aftersales-orders/${item.GUID}`">
            {{ item.number }}
          </router-link>
        </template>
        <template v-slot:item.date="{ item }">
          {{ $moment(item.date).format('L') }}
        </template>
        <template v-slot:item.estimatedDate="{ item }">
          {{ $moment(item.estimatedDate).format('L') }}
        </template>
        <template v-slot:item.status="{ item }">
          {{ $t(`enums.aftersales_orders_statuses.${item.status}`) }}
        </template>
      </v-data-table>
    </main>
  </div>
</template>

<script>
import SalesReportFilter from '@/components/aftersales/SalesReportFilter.vue';
import { mapState } from 'vuex';
import { asSalesReportGroupBy } from '@/utils/enums';

export default {
  name: 'AftersalesOrderList',

  components: {
    SalesReportFilter,
  },

  data() {
    return {
      headers: [
        { text: this.$t('common.date'), value: 'date' },
        { text: this.$t('common.number'), value: 'number' },
        { text: this.$t('common.part'), value: 'part.partNumber' },
        { text: this.$t('views.aftersales_order_list.estimated_date'), value: 'estimatedDate' },
        { text: this.$t('views.aftersales_order_list.total_with_vat'), value: 'part.sumWithVAT' },
        { text: this.$t('common.currency'), value: 'currency' },
        { text: this.$t('common.status'), value: 'status' },
      ],

      items: [],
      options: {
        sortBy: ['date'],
        sortDesc: [true],
      },
      total: 0,

      filter: {
        startDate: new Date((new Date()).getFullYear(), (new Date()).getMonth(), 1).toISOString(),
        endDate: '',
        group: '',
      },

      loading: false,
      isFilterShown: false,
    };
  },
  computed: {
    ...mapState(['workMode']),
  },

  watch: {
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },
  },

  created() {
    this.setWorkModeLayout();
    this.getItems();
  },

  methods: {
    groupByHeader(groupBy, group) {
      if (!group) { return ''; }

      const groupField = this.$t(`enums.aftersales_sales_report_group.${asSalesReportGroupBy[groupBy[0]]}`);

      return `${groupField}: ${group}`;
    },
    sumSubTotal(items = []) {
      return items.reduce((sum, item) => { sum += item.part.sumWithVAT; return sum; }, 0);
    },

    setWorkModeLayout() {
      if (this.workMode.isModeSollers) {
        this.headers.splice(3, 0, ...[
          { text: this.$t('views.aftersales_order_list.dealer'), value: 'dealer.code' },
        ]);
      }
    },

    async getItems() {
      this.loading = true;

      const {
        sortBy, sortDesc,
      } = this.options;

      const params = {};
      params.query = {
        // status: 'ERP_AS_CLOSED',
        ...this.mapFilter(),
      };

      if (sortBy && sortBy.length) {
        params.sort = `${sortDesc[0] ? '-' : ''}${sortBy[0]}`;
      }

      try {
        const { data = [] } = await this.$http.get('/aftersales-orders/sales-report', { params });
        this.items = data.map((item) => ({
          dealer: {},
          ...item,
          // eslint-disable-next-line no-underscore-dangle
          orderPartId: `${item.id}-${item.part._id}`,
          date: item.date && new Date(item.date),
          estimatedDate: item.estimatedDate && new Date(item.estimatedDate),
        }));
        this.total = data.total;
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loading = false;
      }
    },

    toggleFilter() {
      this.isFilterShown = !this.isFilterShown;
    },

    applyFilter(filter) {
      this.filter = { ...filter };
      this.getItems();
    },

    mapFilter() {
      const filter = {};
      const dateFilter = [];
      if (this.filter.startDate) {
        dateFilter.push({ $gte: ['$date', { $toDate: this.$moment(this.filter.startDate) }] });
      }
      if (this.filter.endDate) {
        dateFilter.push({ $lte: ['$date', { $toDate: this.$moment(this.filter.endDate) }] });
      }

      if (dateFilter.length) {
        filter.$expr = { $and: dateFilter };
      }
      return filter;
    },
  },
};
</script>
